function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useEffect, useMemo, useState } from 'react';
import Decimal from 'decimal.js';
import { Typography, TableList, FullRow } from 'seagull';
import { ServicePriceEditor } from "../../../../components/ServicePriceEditor/ServicePriceEditor";
import ServicePriceEditorPopover from "../../../../components/ServicePriceEditorPopover/ServicePriceEditorPopover";
import { useFeatureFlags } from "../../../../hooks/useFeatureFlags";
import { serviceTemplateModel, serviceCostModel, serviceModel } from "../../../../models";
import { ServiceSource } from "../../../../models/service/model";
import { isOneTimeInStages } from "../../../../models/service-template";
import { renderDecimalDiscountTitle, renderRangeDiscountTitle } from "../../../DraftsDashboard/EditDraftsTable/serviceCells/serviceDiscountTitles";
import { utils } from "../../../../utils";
import styles from "./ServiceRowPrice.module.scss";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
function getPriceData(cost) {
  return {
    price: serviceCostModel.isRangePrice(cost) ? {
      minPrice: serviceModel.minPrice({
        cost
      }),
      maxPrice: serviceModel.price({
        cost
      })
    } : serviceModel.price({
      cost
    }),
    hasDiscount: serviceModel.discount({
      cost
    }) != null,
    discountedPrice: serviceCostModel.isRangePrice(cost) ? {
      minPrice: serviceModel.minPriceFinal({
        cost
      }),
      maxPrice: serviceModel.applyDiscount({
        cost
      })
    } : serviceModel.finalPrice({
      cost
    }, 1)
  };
}
export function ServiceRowPrice(_ref) {
  var _localService$cost$di;
  let {
    service,
    onInlineEdit,
    onInputFocus,
    onInputBlur
  } = _ref;
  const {
    INLINE_EDIT_SERVICE_PRICING
  } = useFeatureFlags();
  const [localService, setLocalService] = useState(service);
  const [{
    price,
    hasDiscount,
    discountedPrice
  }, setPriceData] = useState(() => getPriceData(service.cost));
  const [anchorEl, setAnchorEl] = useState(null);
  const [priceEditorOpen, setPriceEditorOpen] = useState(false);
  useEffect(() => {
    setLocalService(service);
  }, [service]);
  useEffect(() => {
    setPriceData(getPriceData(localService.cost));
  }, [localService]);
  const serviceAsService = useMemo(() => {
    const baseService = serviceModel.fromServiceTemplate(service, ServiceSource.Draft);
    return _objectSpread(_objectSpread({}, baseService), {}, {
      cost: localService.cost
    });
  }, [service, localService]);
  const isVariable = serviceTemplateModel.isVariablePrice(localService.cost);
  const unitName = serviceCostModel.formattedUnitNameShort(localService.cost);
  const isInvalidDiscount = (isVariable || serviceTemplateModel.isRangePrice(localService.cost)) && !!((_localService$cost$di = localService.cost.discount) !== null && _localService$cost$di !== void 0 && _localService$cost$di.isValue());
  const renderDecimalDiscountedPrice = discountedPrice => /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Typography, {
      as: "span",
      "data-testid": "service-library-row-discounted-price",
      children: utils.formatDollarAmount(discountedPrice)
    }), !isInvalidDiscount && price instanceof Decimal && hasDiscount && /*#__PURE__*/_jsxs(_Fragment, {
      children: [' ', /*#__PURE__*/_jsxs(Typography, {
        as: "span",
        skin: "disabled",
        textDecoration: "line-through",
        "data-testid": "service-library-row-original-price",
        children: ["$", utils.formatNumberWithCommas(price)]
      })]
    })]
  });
  const renderNonDecimalDiscountedPrice = discountedPrice => /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(Typography, {
      as: "span",
      "data-testid": "service-library-row-discounted-price",
      children: [!!discountedPrice.minPrice && /*#__PURE__*/_jsxs(_Fragment, {
        children: [utils.formatDollarAmount(discountedPrice.minPrice), ' - ']
      }), discountedPrice.minPrice == null && 'Up to ', discountedPrice.maxPrice instanceof Decimal ? utils.formatDollarAmount(discountedPrice.maxPrice) : '']
    }), price != null && !isInvalidDiscount && !(price instanceof Decimal) && hasDiscount && /*#__PURE__*/_jsxs(_Fragment, {
      children: [' ', /*#__PURE__*/_jsxs(Typography, {
        as: "span",
        skin: "disabled",
        textDecoration: "line-through",
        "data-testid": "service-library-row-original-price",
        children: [price.minPrice instanceof Decimal && /*#__PURE__*/_jsx(_Fragment, {
          children: /*#__PURE__*/_jsxs(Typography, {
            as: "span",
            skin: "disabled",
            textDecoration: "line-through",
            "data-testid": "service-library-row-original-price",
            children: [utils.formatDollarAmount(price.minPrice), ' - ']
          })
        }), "".concat(price.maxPrice instanceof Decimal ? utils.formatDollarAmount(price.maxPrice) : '')]
      })]
    })]
  });
  return /*#__PURE__*/_jsxs(FullRow, {
    className: styles.root,
    "data-testid": "service-library-row-price",
    children: [/*#__PURE__*/_jsx(TableList.InlineControl.Button, {
      testid: "service-library-row-price-btn",
      error: false,
      tooltipProps: {
        title: discountedPrice == null ? '' : discountedPrice instanceof Decimal ? renderDecimalDiscountTitle(discountedPrice, price, hasDiscount, isVariable, unitName) : renderRangeDiscountTitle(discountedPrice, price, hasDiscount)
      },
      onClick: e => {
        setAnchorEl(e.currentTarget);
        setPriceEditorOpen(true);
        onInputFocus === null || onInputFocus === void 0 || onInputFocus();
      },
      children: discountedPrice == null ? /*#__PURE__*/_jsx(Typography, {
        "data-testid": "service-library-row-discounted-price",
        children: "________"
      }) : discountedPrice instanceof Decimal ? renderDecimalDiscountedPrice(discountedPrice) : renderNonDecimalDiscountedPrice(discountedPrice)
    }), isVariable && /*#__PURE__*/_jsxs(Typography, {
      className: styles.unitName,
      skin: "secondary",
      ellipsis: true,
      tooltipProps: {
        title: unitName
      },
      children: ["/ ", unitName]
    }), INLINE_EDIT_SERVICE_PRICING ? /*#__PURE__*/_jsx(ServicePriceEditorPopover, {
      serviceOrigin: "template",
      onServiceChange: service => {
        const serviceToServiceTemplate = serviceTemplateModel.fromService(service, localService);
        setLocalService(serviceToServiceTemplate);
        onInlineEdit(serviceToServiceTemplate);
      },
      serviceWithChanges: serviceAsService,
      service: serviceAsService,
      editorAnchor: anchorEl,
      isOpen: priceEditorOpen,
      setIsOpen: setPriceEditorOpen
    }) : /*#__PURE__*/_jsx(ServicePriceEditor, {
      cost: localService.cost,
      editorAnchor: anchorEl,
      isOpen: priceEditorOpen,
      isBillInStages: isOneTimeInStages(service.billing),
      setIsOpen: isOpen => {
        if (!isOpen) onInputBlur === null || onInputBlur === void 0 || onInputBlur();
        setPriceEditorOpen(isOpen);
      },
      onCostChange: cost => {
        setLocalService(_objectSpread(_objectSpread({}, localService), {}, {
          cost
        }));
        onInlineEdit({
          cost
        });
      }
    })]
  });
}